import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faFaceSmileBeam,
  faSealExclamation,
} from 'assets/icons/fontawesome/duotone';
import CharacterLeft from 'assets/images/characters/CarolinaMariadeJesus/hand-raised-without-waving-right.webp';
import CharacterPoint from 'assets/images/characters/CarolinaMariadeJesus/speaking-point.webp';
import {useState} from 'react';
import truncateStringByWords from 'utils/functions/truncateStringByWords';
import {useMobile} from 'utils/hooks/useMobile';
import {useNavigate} from 'react-router-dom';
import {Button} from 'letrus-ui';
import {ZeroGradeComment} from 'APIs/letrusAPI/zeroGradeComment/types';
import ZeroGradeReasons from 'components/ZeroGradeReasons';
import styles from './Highlight.module.scss';

export interface HighlightProps {
  variant: 'positive' | 'attention' | 'zeroGrade';
  subtitle: string;
  comment: string;
  indicator: string;
  onClickMainButton(): void;
  isHighlightModal?: boolean;
  onClickSeeMoreButton?(): void;
  competenceName?: string;
  shouldShowReasonsButton?: boolean;
  zeroGradeReasons?: ZeroGradeComment[];
  genreName?: string;
}

enum HighlightTitle {
  positive = 'Pontos Positivos',
  attention = 'Pontos de atenção',
  zeroGrade = 'Pontos que motivaram o zeramento',
}

enum FeedbackTitle {
  positive = 'Veja seus destaques',
  attention = 'Como melhorar?',
  zeroGrade = '',
}

enum MainButtonText {
  positive = 'Ver pontos de atenção',
  attention = 'Ver nota',
  zeroGrade = 'Entender a correção',
}

enum IconColor {
  positive = '#00c341',
  attention = '#f3af31',
  zeroGrade = '#e94b56',
}

function Highlight({
  variant,
  subtitle,
  comment,
  indicator,
  onClickMainButton,
  isHighlightModal,
  onClickSeeMoreButton,
  competenceName,
  shouldShowReasonsButton,
  zeroGradeReasons,
  genreName,
}: HighlightProps): JSX.Element {
  const [showFullDescriptionText, setShowFullDescriptionText] = useState(false);

  const {isMobile} = useMobile();
  const navigate = useNavigate();

  const formattedComment = comment?.replace(/\n/g, '<br />');

  return (
    <section
      className={`${styles.highlight} ${styles[variant]} ${
        isHighlightModal && styles.isHighlightModal
      }`}
    >
      <div className={styles.header}>
        <FontAwesomeIcon
          icon={variant === 'positive' ? faFaceSmileBeam : faSealExclamation}
          color={IconColor[variant]}
          size="3x"
        />

        <span>
          <div className={styles.titleWrapper}>
            <h2>
              {isHighlightModal
                ? `Foco: ${subtitle} ${competenceName}`
                : HighlightTitle[variant]}
            </h2>

            {variant === 'zeroGrade' &&
              zeroGradeReasons &&
              zeroGradeReasons.length > 0 && (
                <ZeroGradeReasons
                  isZeroGradeReview
                  reasons={zeroGradeReasons}
                  genreName={genreName}
                />
              )}
          </div>

          {isHighlightModal
            ? 'Veja seu principal ponto de atenção da última atividade!'
            : subtitle}
        </span>
      </div>

      {variant !== 'zeroGrade' && (
        <p className={styles.evidence}>
          <b>Evidência: </b>

          <span dangerouslySetInnerHTML={{__html: indicator}} />
        </p>
      )}

      <div className={styles.feedbackCardWrapper}>
        <article
          className={`
            ${styles.feedbackCard}
            ${showFullDescriptionText ? styles.expanded : ''}`}
        >
          <span>
            {variant !== 'zeroGrade' && <h4>{FeedbackTitle[variant]}</h4>}

            <p
              dangerouslySetInnerHTML={{
                __html:
                  showFullDescriptionText || variant === 'positive'
                    ? formattedComment
                    : truncateStringByWords(
                        formattedComment,
                        isMobile ? 12 : 20,
                      ),
              }}
            />
          </span>

          {variant !== 'positive' && (
            <Button
              variant="secondary"
              onClick={() => {
                if (!showFullDescriptionText) {
                  onClickSeeMoreButton?.();
                }
                setShowFullDescriptionText(!showFullDescriptionText);
              }}
            >
              <FontAwesomeIcon icon="eye" />{' '}
              {showFullDescriptionText ? 'Ler menos' : 'Ler mais'}
            </Button>
          )}
        </article>

        {(variant === 'positive' || variant === 'attention') && (
          <img
            src={CharacterLeft}
            aria-hidden
            alt=""
            className={styles.carolinaHoldingLeft}
          />
        )}

        {variant === 'zeroGrade' && (
          <img
            src={CharacterPoint}
            aria-hidden
            alt=""
            className={styles.carolinaHoldingLeft}
          />
        )}
      </div>

      {shouldShowReasonsButton && (
        <Button
          variant="primary"
          onClick={() => navigate('/atividades/pesquisa/708')}
        >
          <FontAwesomeIcon icon="eye" /> Entenda mais sobre os motivos
        </Button>
      )}

      {variant !== 'zeroGrade' && (
        <Button variant="primary" onClick={onClickMainButton}>
          <FontAwesomeIcon icon={isHighlightModal ? 'pencil' : 'eye'} />
          {isHighlightModal ? 'Iniciar a redação' : MainButtonText[variant]}
        </Button>
      )}
    </section>
  );
}

export default Highlight;
